import { CheckIcon, PlusCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { LoaderIcon } from '@/components/ui/loader-icon';
import { OptionsOrFetcher, useDebounce } from '@/components/ui/multi-select-utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { EmptyArray } from '@/lib/api-calls';
import { cn } from '@/lib/tailwind-utils';
import { ArrayOrReadonlyArray } from '@/types/global';
import { partitionBy } from '@airreading/common';
import { useEffect, useState } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

type TValueOption<TValue> = {
  label: string;
  value: TValue;
};

type DropdownOptions<TValue> = ArrayOrReadonlyArray<TValueOption<TValue>>;

interface TableFilterDropdownProps<TValue> {
  title?: string;
  options: OptionsOrFetcher<DropdownOptions<TValue>>;
  selectedValues: TValue[];
  onChange: (selectedValues: TValue[]) => void;
  singleSelect?: boolean;
  isLoading?: boolean;
  valueToString?: (value: TValue) => string;
  getOptionSearchFn?: (value: TValue) => Promise<{ name: string; id: string }>;
  filterOptions?: TValueOption<string>[];
  defaultFilterOption?: string;
  onFilterChange?: (filter: string) => void;
  'data-testid'?: string;
}

export function TableFilterDropdown<TValue>({
  title,
  options,
  onChange,
  selectedValues,
  singleSelect = false,
  isLoading = false,
  valueToString = (value) => String(value),
  getOptionSearchFn = undefined,
  filterOptions = [],
  defaultFilterOption,
  onFilterChange,
  'data-testid': dataTestId,
}: TableFilterDropdownProps<TValue>) {
  // when doing `onSearch()` to fetch options, user may type a keyword, select an option and then type another keyword
  // when showing results for the second keyword, we want to keep the selected option from the previous selection
  // so using `prevValueToLabelMap` to keep prev selected option labels.
  const [prevValueToLabelMap, setPrevValueToLabelMap] = useState<Map<TValue, string>>(new Map());
  const [inputValue, setInputValue] = useState('');
  const [isSearchPending, setIsSearchPending] = useState(false);
  const debouncedSearchTerm = useDebounce(inputValue, 500);

  // static options, no search fn
  const providedOptions = Array.isArray(options) ? options : [];
  // options are fetched from search fn
  const onSearch = typeof options === 'function' ? options : undefined;

  const [selectables, setSelectables] = useState<DropdownOptions<TValue>>(providedOptions);

  useEffect(() => {
    setSelectables(Array.isArray(options) ? options : []);
  }, [options]);

  useEffect(() => {
    const doSearch = async () => {
      setIsSearchPending(true);
      const res = await onSearch?.(debouncedSearchTerm);
      setSelectables(res || EmptyArray);
      setPrevValueToLabelMap((prevMap) => {
        const newMap = new Map(prevMap);
        (res || EmptyArray).forEach(({ label, value }) => {
          newMap.set(value, label);
        });

        return newMap;
      });
      setIsSearchPending(false);
    };

    const exec = async () => {
      if (!onSearch || !open) return;

      if (!debouncedSearchTerm) {
        setSelectables([]);
        return;
      }

      if (debouncedSearchTerm) {
        await doSearch();
      }
    };

    void exec();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, open]);

  // Effect to fetch missing option labels when selectedValues exist but are not in current options
  useEffect(() => {
    if (!getOptionSearchFn || selectedValues.length === 0) return;

    // Find selected values that don't have their labels cached in prevValueToLabelMap
    // These are values that were selected but their display labels need to be fetched
    const missingValues = selectedValues.filter((value) => {
      return !prevValueToLabelMap.has(value);
    });

    if (missingValues.length > 0) {
      const fetchMissingOptions = async () => {
        try {
          const promises = missingValues.map(async (value) => {
            try {
              const result = await getOptionSearchFn(value);
              return { value, label: result.name || String(value) };
            } catch (error) {
              console.warn(`Failed to fetch option for value ${value}:`, error);
              return { value, label: String(value) };
            }
          });

          const fetchedOptions = await Promise.all(promises);

          // Update the previous value to label map with the fetched options
          setPrevValueToLabelMap((prevMap) => {
            const newMap = new Map(prevMap);
            fetchedOptions.forEach(({ value, label }) => {
              newMap.set(value, label);
            });
            return newMap;
          });
        } catch (error) {
          console.error('Error fetching missing options:', error);
        }
      };

      void fetchMissingOptions();
    }
  }, []);

  const selectedOptions: TValueOption<TValue>[] = selectedValues.map((value) => {
    // for selected value, find its display option from the provided options, or search results, or previously searched options
    const option = selectables.find((p) => p.value === value);
    return option ?? { value, label: prevValueToLabelMap.get(value) ?? valueToString(value) };
  });

  const [nonSelectedOptions] = partitionBy(selectables, (option) =>
    selectedOptions.every((p) => p.value !== option.value),
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-dashed" loading={isLoading} data-testid={dataTestId}>
          <PlusCircle className="mr-2 h-4 w-4" />
          {title}
          {selectedValues.length > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                {selectedValues.length}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedValues.length > 2 ? (
                  <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                    {selectedValues.length} selected
                  </Badge>
                ) : (
                  selectedOptions.map((option) => (
                    <Badge
                      variant="secondary"
                      key={valueToString(option.value)}
                      className="rounded-sm px-1 font-normal"
                    >
                      {option.label}
                    </Badge>
                  ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command shouldFilter={!onSearch}>
          <CommandInput
            placeholder={title}
            value={inputValue}
            onValueChange={(value) => {
              setInputValue(value);
            }}
          />
          {filterOptions.length > 0 && onFilterChange && (
            <CommandGroup className="flex flex-row justify-center py-2">
              <RadioGroup
                onValueChange={onFilterChange}
                defaultValue={defaultFilterOption}
                className="flex flex-wrap gap-4"
              >
                {filterOptions.map((option) => {
                  const id = `filter-type-${option.value}`;
                  return (
                    <div key={String(option.value)} className="flex items-center space-x-2">
                      <RadioGroupItem id={id} value={String(option.value)} />
                      <Label htmlFor={id} className="font-normal">
                        {option.label}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </CommandGroup>
          )}
          <CommandSeparator />
          <CommandList>
            {isSearchPending ? (
              <div className="grid place-content-center py-1">
                <LoaderIcon />
              </div>
            ) : (
              <>
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandGroup>
                  {(onSearch ? [selectedOptions, nonSelectedOptions] : selectables).flat().map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <CommandItem
                        key={valueToString(option.value)}
                        onSelect={() => {
                          if (isSelected) {
                            onChange(singleSelect ? [] : selectedValues.filter((value) => value !== option.value));
                          } else {
                            onChange(singleSelect ? [option.value] : [...selectedValues, option.value]);
                          }
                        }}
                      >
                        <div
                          className={cn(
                            'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                            isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible',
                          )}
                        >
                          <CheckIcon className={cn('h-4 w-4')} />
                        </div>
                        {/* {option.icon && <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />} */}
                        <span>{option.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
                {selectedValues.length > 0 && (
                  <>
                    <CommandSeparator />
                    <CommandGroup>
                      <CommandItem onSelect={() => onChange([])} className="justify-center text-center">
                        Clear filters
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
